import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardBody,
  Button,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Spinner,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Tooltip,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { FaDatabase, FaRocket, FaBolt, FaInfo, FaSync } from 'react-icons/fa';

interface SeedingTemplate {
  type: string;
  name: string;
  category: string;
  brands: string[];
}

interface SeedingResult {
  type: string;
  count: number;
}

interface BulkSeedCounts {
  rackets: number;
  shoes: number;
  apparel: number;
  strings: number;
  balls: number;
  bags: number;
}

const AdminSeedingPanel: React.FC = () => {
  const [templates, setTemplates] = useState<SeedingTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [selectedType, setSelectedType] = useState('');
  const [seedCount, setSeedCount] = useState(10);
  const [bulkCounts, setBulkCounts] = useState<BulkSeedCounts>({
    rackets: 20,
    shoes: 15,
    apparel: 25,
    strings: 10,
    balls: 8,
    bags: 12,
  });
  const [isSeeding, setIsSeeding] = useState(false);
  const [lastSeedResults, setLastSeedResults] = useState<SeedingResult[]>([]);

  const toast = useToast();

  // Load templates on component mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'https://h7es6qyuyh.execute-api.eu-west-2.amazonaws.com/dev';
      const response = await fetch(`${apiUrl}/admin/seed/templates`);
      const data = await response.json();
      
      if (data.success) {
        setTemplates(data.data.templates);
        if (data.data.templates.length > 0) {
          setSelectedType(data.data.templates[0].type);
        }
      } else {
        throw new Error(data.error?.message || 'Failed to load templates');
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast({
        title: 'Error Loading Templates',
        description: 'Failed to load seeding templates. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const seedSpecificType = async () => {
    if (!selectedType) {
      toast({
        title: 'No Type Selected',
        description: 'Please select a type to seed.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSeeding(true);
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'https://h7es6qyuyh.execute-api.eu-west-2.amazonaws.com/dev';
      const response = await fetch(`${apiUrl}/admin/seed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: selectedType,
          count: seedCount,
          userId: 'demo-user-' + Date.now(),
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        const newResults = data.data.posts || [];
        setResults(prev => [...newResults, ...prev]);
        
        toast({
          title: 'Seeding Successful!',
          description: `Successfully created ${newResults.length} ${selectedType} posts.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error(data.error?.message || 'Seeding failed');
      }
    } catch (error) {
      console.error('Error seeding posts:', error);
      toast({
        title: 'Seeding Failed',
        description: 'Failed to create posts. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSeeding(false);
    }
  };

  const bulkSeedAll = async () => {
    try {
      setIsSeeding(true);
      const apiUrl = import.meta.env.VITE_API_BASE_URL || 'https://h7es6qyuyh.execute-api.eu-west-2.amazonaws.com/dev';
      const response = await fetch(`${apiUrl}/admin/seed/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          counts: bulkCounts,
          userId: 'demo-user-' + Date.now(),
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setLastSeedResults(data.data.results || []);
        const totalCount = data.data.results?.reduce((acc: number, r: SeedingResult) => acc + r.count, 0) || 0;
        
        toast({
          title: 'Bulk Seeding Successful!',
          description: `Successfully created ${totalCount} total posts across all categories.`,
          status: 'success',
          duration: 7000,
          isClosable: true,
        });
      } else {
        throw new Error(data.error?.message || 'Bulk seeding failed');
      }
    } catch (error) {
      console.error('Error bulk seeding:', error);
      toast({
        title: 'Bulk Seeding Failed',
        description: 'Failed to create bulk posts. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSeeding(false);
    }
  };

  const updateBulkCount = (type: keyof BulkSeedCounts, value: number) => {
    setBulkCounts(prev => ({
      ...prev,
      [type]: Math.max(0, Math.min(value, 50)) // Limit between 0 and 50
    }));
  };

  const getTotalBulkCount = () => {
    return Object.values(bulkCounts).reduce((acc, count) => acc + count, 0);
  };

  if (loading) {
    return (
      <Container maxW="6xl" py={8}>
        <VStack spacing={8} align="center">
          <Spinner size="xl" color="brand.green.medium" thickness="4px" />
          <Text fontSize="lg" color="brand.gray.600">Loading seeding templates...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="6xl" py={8}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <Box textAlign="center">
          <HStack justify="center" mb={4}>
            <FaDatabase color="var(--chakra-colors-brand-green-medium)" size="32" />
            <Heading size="xl" color="brand.green.dark">
              Admin Database Seeding Panel
            </Heading>
          </HStack>
          <Text fontSize="lg" color="brand.gray.600" maxW="800px" mx="auto">
            Generate realistic tennis equipment data for testing and demonstration purposes. 
            This will create actual posts in your database with proper tennis product information.
          </Text>
        </Box>

        {/* Warning Alert */}
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Admin Tool Warning!</AlertTitle>
            <AlertDescription>
              This tool creates real data in your database. Use responsibly and only in development/testing environments.
              Generated posts will be visible to all users browsing the marketplace.
            </AlertDescription>
          </Box>
        </Alert>

        {/* Seeding Tabs */}
        <Tabs variant="enclosed" colorScheme="green">
          <TabList>
            <Tab>
              <HStack>
                <FaBolt />
                <Text>Quick Seed</Text>
              </HStack>
            </Tab>
            <Tab>
              <HStack>
                <FaRocket />
                <Text>Bulk Seed</Text>
              </HStack>
            </Tab>
            <Tab>
              <HStack>
                <FaInfo />
                <Text>Results</Text>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            {/* Quick Seed Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <Heading size="md" color="brand.green.dark">
                  Seed Specific Equipment Type
                </Heading>
                
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }} gap={6}>
                  <GridItem>
                    <Text mb={2} fontWeight="semibold">Equipment Type</Text>
                    <Select 
                      value={selectedType} 
                      onChange={(e) => setSelectedType(e.target.value)}
                      placeholder="Select equipment type"
                    >
                      {templates.map(template => (
                        <option key={template.type} value={template.type}>
                          {template.name} ({template.category})
                        </option>
                      ))}
                    </Select>
                  </GridItem>
                  
                  <GridItem>
                    <Text mb={2} fontWeight="semibold">Number of Items</Text>
                    <NumberInput
                      value={seedCount}
                      onChange={(_, value) => setSeedCount(value)}
                      min={1}
                      max={25}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </GridItem>
                  
                  <GridItem display="flex" alignItems="end">
                    <Button
                      onClick={seedSpecificType}
                      isLoading={isSeeding}
                      loadingText="Creating..."
                      bg="brand.green.medium"
                      color="white"
                      _hover={{ bg: 'brand.green.dark' }}
                      size="lg"
                      width="full"
                    >
                      Generate Posts
                    </Button>
                  </GridItem>
                </Grid>

                {/* Selected Type Info */}
                {selectedType && (
                  <Card>
                    <CardBody>
                      {templates.find(t => t.type === selectedType) && (
                        <VStack align="start" spacing={3}>
                          <HStack>
                            <Badge colorScheme="green" px={3} py={1}>
                              {templates.find(t => t.type === selectedType)?.category}
                            </Badge>
                            <Text fontWeight="semibold">
                              {templates.find(t => t.type === selectedType)?.name}
                            </Text>
                          </HStack>
                          <Box>
                            <Text fontSize="sm" fontWeight="semibold" mb={1}>Available Brands:</Text>
                            <HStack spacing={2} flexWrap="wrap">
                              {templates.find(t => t.type === selectedType)?.brands.map(brand => (
                                <Badge key={brand} variant="outline" size="sm">
                                  {brand}
                                </Badge>
                              ))}
                            </HStack>
                          </Box>
                        </VStack>
                      )}
                    </CardBody>
                  </Card>
                )}
              </VStack>
            </TabPanel>

            {/* Bulk Seed Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <HStack justify="space-between" align="center">
                  <Heading size="md" color="brand.green.dark">
                    Bulk Seed All Categories
                  </Heading>
                  <Stat textAlign="center">
                    <StatLabel>Total Posts</StatLabel>
                    <StatNumber color="brand.green.medium">{getTotalBulkCount()}</StatNumber>
                    <StatHelpText>Will be created</StatHelpText>
                  </Stat>
                </HStack>

                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={4}>
                  {Object.entries(bulkCounts).map(([type, count]) => (
                    <Card key={type}>
                      <CardHeader pb={2}>
                        <Text fontWeight="semibold" textTransform="capitalize">
                          {type}
                        </Text>
                      </CardHeader>
                      <CardBody pt={0}>
                        <NumberInput
                          value={count}
                          onChange={(_, value) => updateBulkCount(type as keyof BulkSeedCounts, value)}
                          min={0}
                          max={50}
                          size="sm"
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </CardBody>
                    </Card>
                  ))}
                </Grid>

                <Button
                  onClick={bulkSeedAll}
                  isLoading={isSeeding}
                  loadingText="Creating All Posts..."
                  bg="brand.primary"
                  color="black"
                  _hover={{ bg: 'brand.primaryHover' }}
                  size="lg"
                  fontSize="lg"
                  py={6}
                >
                  🚀 Create {getTotalBulkCount()} Posts Across All Categories
                </Button>

                {lastSeedResults.length > 0 && (
                  <Card>
                    <CardHeader>
                      <Text fontWeight="semibold">Last Bulk Seed Results</Text>
                    </CardHeader>
                    <CardBody>
                      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
                        {lastSeedResults.map(result => (
                          <Box key={result.type} textAlign="center">
                            <Text fontWeight="semibold" textTransform="capitalize">
                              {result.type}
                            </Text>
                            <Text fontSize="2xl" color="brand.green.medium" fontWeight="bold">
                              {result.count}
                            </Text>
                            <Text fontSize="sm" color="gray.500">posts created</Text>
                          </Box>
                        ))}
                      </Grid>
                    </CardBody>
                  </Card>
                )}
              </VStack>
            </TabPanel>

            {/* Results Tab */}
            <TabPanel>
              <VStack spacing={6} align="stretch">
                <HStack justify="space-between">
                  <Heading size="md" color="brand.green.dark">
                    Recently Created Posts
                  </Heading>
                  <Tooltip label="Refresh templates">
                    <IconButton
                      aria-label="Refresh"
                      icon={<FaSync />}
                      onClick={loadTemplates}
                      variant="ghost"
                      colorScheme="green"
                    />
                  </Tooltip>
                </HStack>

                {results.length === 0 ? (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>No Recent Results</AlertTitle>
                      <AlertDescription>
                        No posts have been created yet. Use the Quick Seed or Bulk Seed tabs to generate some data.
                      </AlertDescription>
                    </Box>
                  </Alert>
                ) : (
                  <Card>
                    <CardBody>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Title</Th>
                            <Th>Price</Th>
                            <Th>Condition</Th>
                            <Th>ID</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {results.slice(0, 20).map((post, index) => (
                            <Tr key={index}>
                              <Td fontWeight="semibold" maxW="300px" isTruncated>
                                {post.title}
                              </Td>
                              <Td>
                                <Text fontWeight="bold" color="brand.green.medium">
                                  ${post.price}
                                </Text>
                              </Td>
                              <Td>
                                <Badge 
                                  colorScheme={
                                    post.condition === 'new' ? 'green' :
                                    post.condition === 'like-new' ? 'blue' :
                                    post.condition === 'good' ? 'yellow' : 'orange'
                                  }
                                >
                                  {post.condition}
                                </Badge>
                              </Td>
                              <Td fontSize="xs" color="gray.500">
                                {post.postId?.slice(0, 8)}...
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                      {results.length > 20 && (
                        <Text textAlign="center" mt={4} fontSize="sm" color="gray.500">
                          Showing 20 of {results.length} results
                        </Text>
                      )}
                    </CardBody>
                  </Card>
                )}
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </Container>
  );
};

export default AdminSeedingPanel; 